import { useEffect, useState } from "react";
import { Trophy, RefreshCcw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const when = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

const Match = ({ m, live }) => (
  <div className="rounded-3xl bg-white border border-stone-200 p-3" data-testid={`cricket-${m.match_id}`}>
    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-stone-500">
      <span>{m.series}</span><span>{m.match_format}</span>
    </div>
    <div className="mt-1.5 space-y-0.5">
      {(m.teams || []).map((t, i) => (
        <div key={i} className="flex items-center justify-between">
          <span className="font-black text-stone-900 text-sm">{t.short || t.name}</span>
          <span className="font-mono font-bold text-stone-700 text-sm">{t.score || "—"}</span>
        </div>
      ))}
    </div>
    <div className="text-[11px] text-stone-600 mt-1 flex items-center justify-between">
      <span className="line-clamp-1">{m.status}</span>
      {live && m.is_live && <span className="inline-flex items-center gap-1 text-red-600 font-black"><span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" /> LIVE</span>}
    </div>
    {m.venue && <div className="text-[10px] text-stone-400 mt-0.5">{m.venue}</div>}
  </div>
);

// Real provider scores only. "unavailable"/"stale" are shown as such — never demo cards or fantasy points.
export const CricketPanel = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (refresh = false) => {
    setBusy(true);
    return api.get("/cricket/live", { params: refresh ? { refresh: 1 } : {} })
      .then((r) => setD(r.data))
      .catch(() => setD({ status: "unavailable", matches: [], reason: "Could not reach find2near." }))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
    const iv = setInterval(() => { if (document.visibilityState === "visible") load(); }, 60_000);
    return () => clearInterval(iv);
  }, []);
  const live = d?.status === "live";
  return (
    <section className="mb-6" data-testid="cricket-widget">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] uppercase tracking-widest font-black text-stone-700 inline-flex items-center gap-2">
          <Trophy size={14} className="text-sky-600" /> Cricket scores
          {d && <span className={`text-[9px] font-black uppercase rounded-full px-2 py-0.5 ${live ? "bg-red-100 text-red-700" : d.status === "stale" ? "bg-amber-100 text-amber-800" : "bg-stone-200 text-stone-600"}`} data-testid="cricket-status">{d.status}</span>}
        </div>
        <button onClick={() => load(true)} disabled={busy} className="text-[11px] font-bold text-stone-500 hover:text-stone-900 inline-flex items-center gap-1" data-testid="cricket-refresh">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCcw size={11} />} Refresh
        </button>
      </div>
      {d?.matches?.length > 0 ? (
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
          {d.matches.map((m) => <Match key={m.match_id} m={m} live={live} />)}
        </div>
      ) : d && (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-white p-4 text-sm text-stone-600" data-testid="cricket-unavailable">
          Live cricket is unavailable. {d.reason}
        </div>
      )}
      {d && (
        <div className="text-[10px] text-stone-400 mt-1" data-testid="cricket-meta">
          Provider: {d.provider?.name || "none configured"} · last successful update {when(d.last_success_at)} · checked {when(d.checked_at)}
        </div>
      )}
    </section>
  );
};
